/**
 * Top-level storefront segments an operator cannot claim for a built page.
 *
 * Only the FIRST segment matters — `travel-advice/packing` is fine even when
 * `travel-advice` would not be, because the collision surface is the root of
 * the URL space.
 *
 * The list is tiered because the tiers have different reasons to exist, and
 * conflating them is how a list like this rots:
 *
 *   PLATFORM  claiming one breaks the storefront outright
 *   BUILDER   the page builder's own routes, reserved before they exist
 *   CONTENT   pages the storefront already ships; reserved for now, and the
 *             tier that would be revisited if built pages are ever promoted
 *             to root-level addresses
 *
 * A companion spec in traveler-web reads the real route tree and fails when a
 * top-level route is missing from this list, so adding a storefront route
 * without reserving it breaks CI rather than silently shadowing an operator's
 * published page. That test is the point — the reference implementation's
 * equivalent list rotted precisely because nothing checked it.
 */

/** Claiming one of these breaks the storefront. */
export const RESERVED_PLATFORM_SEGMENTS = [
  // Platform internals. `sites` and `cms-site` are internal rewrite
  // destinations — middleware 404s them when requested directly, so a page
  // claiming either would be unreachable at the address it was given.
  "api",
  "sites",
  "cms-site",
  "media",
  "claim",
  // Where a booking link says why it could not be used. Split out from `claim`
  // when that became a route handler that renders nothing — the message needed
  // an address of its own, and it has to be reserved for the same reason
  // `claim` is: a page published here would shadow the only thing a traveller
  // with a dead link ever sees.
  "booking-link",
  "sign-in",
  // Where every password email lands: `/set-password/?token=…` sets a first
  // password or replaces a forgotten one (ADR-0022 D8).
  "set-password",
  // Signing out is a ROUTE, not a button action, and it has to stay one.
  // The account menu renders in the header of every page, and a server action
  // there could not reliably end the session — it threw before clearing
  // anything, so the traveller saw an error and stayed signed in. Navigating
  // to this page runs the sign-out somewhere it can actually happen.
  "sign-out",
  "_next",
  "_unknown-tenant",
  // Local-only component galleries (`/dev/package-states`). The pages call
  // `notFound()` on any deployed environment, but the ROUTE still exists in the
  // production bundle — and an app route wins over the CMS rewrite — so a page
  // published at this address would be shadowed rather than served. Reserving
  // the segment is what stops an operator claiming it in the first place, which
  // is the protective direction.
  //
  // ⚠ This list is NOT traveler-web-only. It feeds
  // `REDIRECT_SOURCE_DENIED_SEGMENTS` (`apps/redirections.ts`) and
  // operator-api's `SYSTEM_RESERVED` (`routing/domain/route-path.ts`), so
  // adding a segment here makes any EXISTING page or redirect under it fail
  // validation on its next save or CSV import — with no backfill scan to find
  // such rows first. `dev` is an unlikely storefront address and no migration
  // was run for it; a segment more plausible as real content needs one.
  "dev",
  // The Meta tour catalog feed (`/feeds/meta-catalog.csv`) that Meta fetches
  // every hour. A page here would shadow it and empty the agency's catalog.
  // Like `dev`, an unlikely content address, so no migration was run for it.
  "feeds",
  // Commerce
  "cart",
  "checkout",
  "confirm",
  // Settling the outstanding balance on a deposit-paid booking. Reserved with
  // its commerce siblings: a published page here would shadow the only route a
  // traveller has for paying what they still owe.
  "pay",
  // Traveller account. `billing`, `profile` and `security` look nested but are
  // NOT — they live under `(account)/(settings)/`, and since both parents are
  // route groups they occupy root-level URLs.
  "account",
  "bookings",
  "dashboard",
  "overview",
  "billing",
  "profile",
  "security",
  // Catalog
  "tours",
  "destinations",
  "tour-types",
  // Utility
  "booking-view",
  // Machine-readable endpoints. Listed for documentation: every one contains a
  // dot, and both the slug rule and the middleware matcher exclude dotted
  // paths, so they cannot be typed as a page address in the first place.
  "robots.txt",
  "sitemap.xml",
  "manifest.webmanifest",
  "llms.txt",
  "favicon.ico",
  // Serves the per-tenant browser-tab icon. Referenced by a constant url from
  // the static root layout, which is what lets a static shell carry a
  // per-operator icon at all — the route resolves the agency from the host.
  "brand-icon.svg",
  ".well-known",
] as const;

/**
 * The page builder's own routes.
 *
 * Built content is addressed at the ROOT, so this tier no longer prefixes
 * anything an operator writes — it holds the few segments the builder itself
 * serves from. `blog` routes the post index; `posts` is reserved beside it
 * because the reference implementation reserved one name while routing the
 * other, and its own design doc flagged the resulting hole. `category`,
 * `preview` and `search` are held for surfaces that do not exist yet.
 */
/**
 * Where the list of an agency's posts is served.
 *
 * NOT a prefix on a post's own address — a post lives at `/<slug>/`, alongside
 * pages, in one flat namespace. This segment routes the INDEX (AC-14), which
 * pages never had. Declared in the same file that reserves it so the live route
 * and its reservation cannot drift apart: a rename touching only one of the two
 * would hand operators the segment the index is served from.
 */
export const POST_INDEX_SEGMENT = "blog";

/**
 * Where a signed preview link lands: `/preview/?token=…`.
 *
 * Named here for the same reason `POST_INDEX_SEGMENT` is — the live route and
 * its reservation are one edit, so a rename cannot free the segment for an
 * operator to claim while traffic is still being sent to it.
 */
export const PREVIEW_SEGMENT = "preview";

/**
 * Where a writer's public page is served: `/author/{slug}/`
 * (blog-author-page AC-02). Named here so the route and its reservation stay
 * one edit.
 */
export const AUTHOR_SEGMENT = "author";

export const RESERVED_BUILDER_SEGMENTS = [
  POST_INDEX_SEGMENT,
  AUTHOR_SEGMENT,
  "posts",
  // The blog category archive's base: `/category/{slug}/`
  // (feature: blog-category-page), rewritten to `sites/[agency]/category/[slug]`
  // in traveler-web's `proxy.ts`. An operator page at `/category` would
  // take the segment and leave the archive nowhere to land.
  "category",
  // Reserved without routing anything today. `pages` was the page prefix before
  // addresses were promoted to the root and is still the internal rewrite
  // destination's segment; leaving it claimable would let an operator publish a
  // page at `/pages/` whose own children then read as `/pages/<child>` — an
  // address shape the storefront used to own. Cheaper to keep than to explain.
  "pages",
  PREVIEW_SEGMENT,
  "search",
] as const;

/**
 * Content pages the storefront already ships. Reserved for now: the page
 * builder does not touch them and does not serve them.
 *
 * `about` and `contact` are NOT here: their built-in pages were removed
 * (feature: about-contact-removal), so an agency builds them as ordinary pages.
 */
export const RESERVED_CONTENT_SEGMENTS = [
  "privacy",
  "terms",
  "cookies",
] as const;

export const RESERVED_STOREFRONT_SEGMENTS: readonly string[] = [
  ...RESERVED_PLATFORM_SEGMENTS,
  ...RESERVED_BUILDER_SEGMENTS,
  ...RESERVED_CONTENT_SEGMENTS,
];

const RESERVED = new Set<string>(RESERVED_STOREFRONT_SEGMENTS);

/**
 * Is `segment` reserved? Compares case-insensitively: addresses are
 * lowercased before storage, so a capitalised input must not slip past.
 */
export function isReservedStorefrontSegment(segment: string): boolean {
  return RESERVED.has(segment.trim().toLowerCase());
}

/**
 * Is the first segment of `path` reserved? Accepts a full page path
 * (`travel-advice/packing`) with or without a leading slash, and only ever
 * inspects the first segment.
 */
export function hasReservedTopLevelSegment(path: string): boolean {
  const first = path.replace(/^\/+/, "").split("/")[0] ?? "";
  return first.length > 0 && isReservedStorefrontSegment(first);
}
